import { DateValue, isToday, isYesterday, toDate } from "@/utils/Dates";
import { compact, identity, memoize } from "lodash";
import { Language } from "./languages";
import { TMessage } from "./tt";
import { when } from "@/utils/functions";
import { logWarn } from "@/utils/log";

export type DateFormatOptions = { withoutYear?: boolean; relative?: boolean };
export type TimeFormatOptions = { withSeconds?: boolean; withoutPrefix?: boolean };
export type DateTimeFormatOptions = DateFormatOptions & TimeFormatOptions;

const localeMapping: Record<Language, string> = {
  [Language.NO]: "no-NB",
  [Language.EN]: "en-GB",
  [Language.SV]: "sv-SE",
};

const makeYearMonthFormat = memoize(
  (lang: Language) =>
    new Intl.DateTimeFormat([localeMapping[lang]], {
      month: "long",
      year: "numeric",
    }),
);

const makeDateFormat = memoize(
  (lang: Language) =>
    new Intl.DateTimeFormat([localeMapping[lang]], {
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
);

const makeDateWithoutYearFormat = memoize(
  (lang: Language) =>
    new Intl.DateTimeFormat([localeMapping[lang]], {
      day: "numeric",
      month: "short",
    }),
);

const makeTimeFormat = memoize(
  (lang: Language) =>
    new Intl.DateTimeFormat([localeMapping[lang]], {
      hour: "2-digit",
      minute: "2-digit",
    }),
);

const makeTimeWithSecondsFormat = memoize(
  (lang: Language) =>
    new Intl.DateTimeFormat([localeMapping[lang]], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
);

const makeRelativeTimeFormat = memoize(
  (lang: Language) => new Intl.RelativeTimeFormat([localeMapping[lang]], { numeric: "auto" }),
);

const makeWeekdayFormat = memoize(
  (lang: Language, format: "long" | "short" | "narrow") =>
    new Intl.DateTimeFormat([localeMapping[lang]], {
      weekday: format,
    }),
  (lang: Language, format: "long" | "short" | "narrow") => `${lang}-${format}`,
);

export const formatYear = (input: number | string | Date | null | undefined): string => {
  if (!input) {
    return "";
  }
  return String(toDate(input).getFullYear());
};

export const formatYearMonth = (
  lang: Language,
  input: number | string | Date | null | undefined,
): string => {
  if (!input) {
    return "";
  }
  try {
    return makeYearMonthFormat(lang).format(toDate(input));
  } catch (e) {
    logWarn(e, `formatYearMonth invalid input: ${input}`);
    return String(input);
  }
};

export function formatDate(
  lang: Language,
  input: number | string | Date | null | undefined,
  options?: DateFormatOptions,
): string {
  try {
    switch (true) {
      case !input:
        return "";
      case typeof input === "string" && /^\d{4}$/.test(input):
        return input;
      case options?.relative && input && isToday(input):
        return makeRelativeTimeFormat(lang).format(0, "day");
      case options?.relative && input && isYesterday(input):
        return makeRelativeTimeFormat(lang).format(-1, "day");
      case options?.withoutYear:
        return makeDateWithoutYearFormat(lang).format(toDate(input));
      default:
        return makeDateFormat(lang).format(toDate(input));
    }
  } catch (e) {
    logWarn(e, `formatDate invalid input ${input}`);
    return String(input);
  }
}

export function formatTime(
  lang: Language,
  input:
    | DateValue
    | null
    | undefined
    | { startTime: DateValue | null | undefined; endTime: DateValue | null | undefined },
  options?: TimeFormatOptions,
): string {
  if (!input) {
    return "";
  }

  const wrapInDate = (input: DateValue | null | undefined) => {
    if (!input) {
      return null;
    }
    const isTimeString = typeof input === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(input);
    return toDate(isTimeString ? `2000-01-01T${input}` : input);
  };

  try {
    const { startTime, endTime } =
      typeof input === "object" && "startTime" in input
        ? input
        : { startTime: input, endTime: null };
    const prefix =
      !options?.withoutPrefix && (lang === Language.NO || lang === Language.SV) ? "kl " : "";
    const f = options?.withSeconds ? makeTimeWithSecondsFormat(lang) : makeTimeFormat(lang);
    const start = when(wrapInDate(startTime), (d) => f.format(d));
    const end = when(wrapInDate(endTime), (d) => f.format(d));

    return prefix + compact([start, end]).join(" - ");
  } catch (e) {
    logWarn(e, `formatTime invalid input`, { input });
    return typeof input === "object" && "startTime" in input
      ? `${input.startTime} - ${input.endTime}`
      : String(input);
  }
}

export function formatDateTime(
  lang: Language,
  input: number | string | Date | null | undefined,
  options?: DateTimeFormatOptions,
): string {
  if (!input) {
    return "";
  }
  try {
    const d = toDate(input);
    return `${formatDate(lang, d, options)}, ${formatTime(lang, d, options)}`;
  } catch (e) {
    logWarn(e, `formatDateTime invalid input: ${input}`);
    return String(input);
  }
}

export function formatDateAndTime(
  locale: Language,
  dateInput: DateValue | null | undefined,
  timeInput:
    | DateValue
    | null
    | undefined
    | {
        startTime: DateValue | null | undefined;
        endTime: DateValue | null | undefined;
      },
  options?: DateTimeFormatOptions,
): string {
  if (!dateInput) {
    return "";
  }
  return [formatDate(locale, dateInput, options), formatTime(locale, timeInput, options)]
    .filter(identity)
    .join(", ");
}

export function formatWeekday(
  lang: Language,
  input: number | string | Date | null | undefined,
): string {
  if (!input) {
    return "";
  }
  return makeWeekdayFormat(lang, "long").format(toDate(input));
}

export function formatWeekdayDateTime(
  lang: Language,
  input: number | string | Date | null | undefined,
): string {
  if (!input) {
    return "";
  }
  try {
    const d = toDate(input);
    const weekday = makeWeekdayFormat(lang, "long").format(d);
    const day = d.getDate();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    const time = formatTime(lang, d, { withoutPrefix: true });
    return `${weekday} ${day}.${month}.${year} kl. ${time}`;
  } catch (e) {
    logWarn(e, `formatWeekdayDateTime invalid input: ${input}`);
    return String(input);
  }
}

export const formatWeekdays = (lang: Language, format: "long" | "short" | "narrow" = "long") => {
  const weekdayFormat = makeWeekdayFormat(lang, format);
  return [
    "2024-07-01",
    "2024-07-02",
    "2024-07-03",
    "2024-07-04",
    "2024-07-05",
    "2024-07-06",
    "2024-07-07",
  ].map((s) => weekdayFormat.format(toDate(s)));
};

export function relativeTime(date: Date | string, locale: Language): TMessage {
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else {
    d = toDate(date);
  }

  if (isToday(d)) {
    return { key: "today-at", params: { t: formatTime(locale, d) } };
  } else if (isYesterday(d)) {
    return { key: "yesterday-at", params: { t: formatTime(locale, d) } };
  } else {
    return { translated: formatDateTime(locale, d) };
  }
}
